import {
  MyActivitiesState,
  MyActivitiesStatePagination,
  MyActivitiesType,
  MyActivityPayment,
} from './myActivitiesTypes';
import {
  MyActivitiesThunkAction,
  SET_MY_ACTIVITIES,
  SET_SELECTED_ACTIVITY_PAYMENTS,
  SetMyActivitiesActionType,
  SetSelectedActivityPaymentsActionType,
} from './myActivitiesActionTypes';
import { MY_ACTIVITIES_NAMESPACE } from './myActivitiesReducer';
import { getSortedMyActivitiesByStartTime } from './myActivitiesUtils';
import MyActivitiesApi from '../../API/my-activities/my-activities.api';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import { MyActivityPaginationResponseDto } from '../../API/event/dto/responses/MyActivityPaginationResponse.Dto';
import { MyActivityImpl } from '../../API/my-activities/implements/my-activity.impl';

export function setMyActivitiesAction(
  payload: MyActivitiesState
): SetMyActivitiesActionType {
  return {
    type: SET_MY_ACTIVITIES,
    payload,
  };
}

export function setSelectedActivityPaymentsAction(
  payments: MyActivityPayment[]
): SetSelectedActivityPaymentsActionType {
  return {
    type: SET_SELECTED_ACTIVITY_PAYMENTS,
    payments,
  };
}

export function GetMyActivitiesThunk(
  type: MyActivitiesType,
  take: number,
  config?: {
    skipEvents?: number;
    skipRooms?: number;
    dontFetchEvents?: boolean;
    dontFetchRooms?: boolean;
  }
): MyActivitiesThunkAction<Promise<MyActivitiesState>> {
  return async (dispatch, getState) => {
    const state = getState()[MY_ACTIVITIES_NAMESPACE];
    const emptyPromise = (
      totalResult: number
    ): Promise<{ data: MyActivityPaginationResponseDto }> => {
      return new Promise<{ data: MyActivityPaginationResponseDto }>(
        (resolve) => {
          const emptyData: { data: MyActivityPaginationResponseDto } = {
            data: {
              myActivities: [],
              totalResults: totalResult,
              totalPages: 0,
            },
          };
          resolve(emptyData);
        }
      );
    };

    const fetchEventsPromise = config?.dontFetchEvents
      ? emptyPromise(state.pagination.eventsTotalResult)
      : MyActivitiesApi.getEventMyActivities(
          type,
          take,
          config?.skipEvents || 0
        );

    const fetchRoomsPromise = config?.dontFetchRooms
      ? emptyPromise(state.pagination.roomsTotalResult)
      : MyActivitiesApi.getResourceMyActivities(
          type,
          take,
          config?.skipRooms || 0
        );

    const myActivities = await Promise.all([
      fetchEventsPromise,
      fetchRoomsPromise,
    ]);

    const myActivitiesData = getSortedMyActivitiesByStartTime(
      [myActivities[0].data.myActivities, myActivities[1].data.myActivities],
      take,
      type
    );

    const newState: MyActivitiesState = {
      myActivities: myActivitiesData.activities,
      selectedActivityPayments: [],
      pagination: {
        eventsTotalResult: myActivities[0].data.totalResults,
        roomsTotalResult: myActivities[1].data.totalResults,
        eventsShown: myActivitiesData.eventCount,
        roomsShown: myActivitiesData.roomCount,
      },
    };
    return newState;
  };
}

export function FetchMyActivitiesThunk(
  type: MyActivitiesType,
  take: number,
  reset?: boolean
): MyActivitiesThunkAction<Promise<void>> {
  return async (dispatch) => {
    if (reset) {
      const resetActivitiesState: MyActivitiesState = {
        myActivities: [],
        selectedActivityPayments: [],
        pagination: {
          eventsTotalResult: 0,
          roomsTotalResult: 0,
          eventsShown: 0,
          roomsShown: 0,
        },
      };
      dispatch(setMyActivitiesAction(resetActivitiesState));
    }
    await dispatch(startLoaderThunk());

    const newState = await dispatch(GetMyActivitiesThunk(type, take));

    dispatch(setMyActivitiesAction(newState));
    return await dispatch(stopLoaderThunk());
  };
}

export function FetchMoreMyActivitiesThunk(
  type: MyActivitiesType,
  take: number
): MyActivitiesThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    await dispatch(startLoaderThunk());
    const activitiesState = getState()[MY_ACTIVITIES_NAMESPACE];
    const activities = activitiesState.myActivities;
    const pagination = activitiesState.pagination;

    const fetchedActivities = await dispatch(
      GetMyActivitiesThunk(type, take, {
        skipEvents: pagination.eventsShown,
        skipRooms: pagination.roomsShown,
        dontFetchEvents: pagination.eventsShown >= pagination.eventsTotalResult,
        dontFetchRooms: pagination.roomsShown >= pagination.roomsTotalResult,
      })
    );

    const newState: MyActivitiesState = {
      myActivities: [...activities, ...fetchedActivities.myActivities],
      selectedActivityPayments: [],
      pagination: {
        eventsTotalResult: fetchedActivities.pagination.eventsTotalResult,
        roomsTotalResult: fetchedActivities.pagination.roomsTotalResult,
        eventsShown:
          fetchedActivities.pagination.eventsShown + pagination.eventsShown,
        roomsShown:
          fetchedActivities.pagination.roomsShown + pagination.roomsShown,
      },
    };

    dispatch(setMyActivitiesAction(newState));
    return await dispatch(stopLoaderThunk());
  };
}

//Add new Thunk (get order payments by activity Id)
export function FetchMyActivityPaymentsThunk(
  id: number
): MyActivitiesThunkAction<Promise<void>> {
  return async (dispatch) => {
    await dispatch(startLoaderThunk());

    const response = await MyActivitiesApi.getOrderPaymentsById(id);
    dispatch(setSelectedActivityPaymentsAction(response.data));

    await dispatch(stopLoaderThunk());
  };
}
